import type { ComponentType } from "preact";
import { useEffect } from "preact/hooks";

import type { Event } from "../types";

import { AppleMusic } from "../components/brand/appleMusic";
import { Deezer } from "../components/brand/deezer";
import { Facebook } from "../components/brand/facebook";
import { Instagram } from "../components/brand/instagram";
import { LastFm } from "../components/brand/lastFm";
import { MusicBrainz } from "../components/brand/musicBrainz";
import { Soundcloud } from "../components/brand/soundcloud";
import { Spotify } from "../components/brand/spotify";
import { Tidal } from "../components/brand/tidal";
import { TikTok } from "../components/brand/tiktok";
import { Unkown } from "../components/brand/unkown";
import { Wikipedia } from "../components/brand/wikipedia";
import { X } from "../components/brand/x";
import { YouTube } from "../components/brand/youtube";
import { YouTubeEmbed, extractYouTubeId } from "../components/embeds/YouTubeEmbed";
import { BackButton } from "../components/ui/BackButton";
import { ImageWithSkeleton } from "../components/ui/ImageWithSkeleton";
import { EventDetailSkeleton } from "../components/ui/Skeleton";
import { useEdition } from "../hooks/useEdition";
import { useTranslations } from "../hooks/useTranslations";
import { useEvent } from "../hooks/useWidgetQueries";
import { formatDate, formatTime, getDisplayDate } from "../lib/dateUtils";
import { getImageUrl } from "../lib/image";
import {
  getLocalizedCustomFields,
  getLocalizedName,
  getLocalizedValue,
} from "../lib/localization";
import { useNavigation } from "../lib/router";

type EventDetailPageProps = {
  eventId: string;
  onNavigate: (path: string) => void;
  onBack: () => void;
};

export function EventDetailPage({
  eventId,
  onNavigate,
  onBack,
}: EventDetailPageProps) {
  const { editionId, locale, edition, timeFormat } = useEdition();
  const t = useTranslations();
  const { toArtist } = useNavigation();
  const { data: event, loading } = useEvent(eventId, editionId);
  const fallback = edition?.default_locale_code;

  const eventName = event ? getLocalizedName(event, locale, fallback) : null;

  useEffect(() => {
    if (eventName && edition?.festival?.name) {
      document.title = `${eventName} - ${edition.festival.name}`;
    }
  }, [eventName, edition]);

  if (loading) {
    return (
      <div className="festapp-event-detail-page">
        <BackButton onClick={onBack} />
        <EventDetailSkeleton />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="festapp-event-detail-page">
        <BackButton onClick={onBack} />
        <div className="festapp-error">
          <p>{t.eventNotFound}</p>
        </div>
      </div>
    );
  }

  const youtubeLink = event?.links?.find(
    (link) => link?.url && extractYouTubeId(link.url) !== null,
  );

  const socialLinks = (event?.links ?? []).filter(
    (link) => link && !(link.url && extractYouTubeId(link.url) !== null),
  );

  const eventImageUrl = getImageUrl(event.image, "medium");
  const description = getLocalizedValue(event, "description", locale, fallback);
  const hasMedia = !!youtubeLink;

  const CalendarIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="64"
      height="64"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
      <line x1="16" x2="16" y1="2" y2="6" />
      <line x1="8" x2="8" y1="2" y2="6" />
      <line x1="3" x2="21" y1="10" y2="10" />
    </svg>
  );

  return (
    <div className="festapp-event-detail-page">
      <BackButton onClick={onBack} />

      <div className="festapp-event-detail">
        {/* Hero */}
        <div className="festapp-event-hero">
          <div className="festapp-event-hero-image-container">
            <ImageWithSkeleton
              src={eventImageUrl}
              alt={eventName ?? ""}
              className="festapp-event-hero-image"
              fallbackIcon={CalendarIcon}
            />
          </div>
          <h1 className="festapp-event-hero-name">{eventName}</h1>
          {event?.categories.length > 0 && (
            <p className="festapp-event-hero-categories">
              {event.categories
                .map((cat) => getLocalizedName(cat, locale, fallback))
                .filter(Boolean)
                .join(", ")}
            </p>
          )}
        </div>

        {/* Schedules - moved up for prominence */}
        {event.schedules.length > 0 && (
          <section className="festapp-event-section">
            <h2 className="festapp-section-title">{t.schedule}</h2>
            <ul className="festapp-event-schedules">
              {event.schedules.map((schedule) => (
                <li key={schedule._id} className="festapp-event-schedule-item">
                  <div className="festapp-event-schedule-date">
                    <span className="festapp-event-schedule-day">
                      {formatDate(
                        getDisplayDate(
                          schedule.start_date,
                          schedule.start_time,
                          edition?.day_start_time ?? null,
                          edition?.start_date ?? null,
                        ),
                        "dddd, LL",
                        locale,
                      )}
                    </span>
                    {schedule.start_time && (
                      <span className="festapp-event-schedule-time">
                        {formatTime(schedule.start_time, timeFormat)}
                        {schedule.end_time &&
                          ` - ${formatTime(schedule.end_time, timeFormat)}`}
                      </span>
                    )}
                  </div>
                  {schedule.place && (
                    <span className="festapp-event-schedule-place">
                      {getLocalizedName(schedule.place, locale, fallback)}
                    </span>
                  )}
                  {schedule.ticket_url && (
                    <a
                      href={schedule.ticket_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="festapp-schedule-item-ticket"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
                        <path d="M13 5v2" />
                        <path d="M13 17v2" />
                        <path d="M13 11v2" />
                      </svg>
                      <span>{t.getTickets}</span>
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Performers */}
        {event.artists.length > 0 && (
          <section className="festapp-event-section">
            <h2 className="festapp-section-title">{t.performers}</h2>
            <ul className="festapp-event-performers">
              {event.artists.map((artist) => (
                <PerformerItem
                  key={artist._id}
                  artist={artist}
                  onClick={() => onNavigate(toArtist(artist._id))}
                />
              ))}
            </ul>
          </section>
        )}

        {/* Description */}
        {description && (
          <section className="festapp-event-section">
            <p className="festapp-event-description">{description}</p>
          </section>
        )}

        {/* Social Links */}
        {socialLinks.length > 0 && (
          <section className="festapp-event-section">
            <div className="festapp-event-links">
              {socialLinks.map((link) => {
                const { Icon, label } = getLinkIcon(link.url);
                return (
                  <a
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="festapp-event-link"
                    title={label}
                  >
                    <Icon className="festapp-event-link-icon" />
                  </a>
                );
              })}
            </div>
          </section>
        )}

        {/* Media Section (Collapsible) */}
        {hasMedia && (
          <section className="festapp-event-section">
            <div className="festapp-media-content">
              {youtubeLink && (
                <div className="festapp-media-item">
                  <YouTubeEmbed url={youtubeLink.url} />
                </div>
              )}
            </div>
          </section>
        )}

        {/* Custom Fields */}
        {event.custom_fields && event.custom_fields.length > 0 && (() => {
          const localizedCustomFields = getLocalizedCustomFields(
            event.custom_fields,
            locale,
            fallback
          );
          return localizedCustomFields.length > 0 ? (
            <section className="festapp-event-section">
              <dl className="festapp-custom-fields">
                {localizedCustomFields.map((field, index) => (
                  <div
                    key={`${field.label}-${index}`}
                    className="festapp-custom-field"
                  >
                    <dt>{field.label}</dt>
                    <dd>{field.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : null;
        })()}
      </div>
    </div>
  );
}

function PerformerItem({
  artist,
  onClick,
}: {
  artist: Event["artists"][number];
  onClick: () => void;
}) {
  const artistImageUrl = getImageUrl(artist.image, "medium");

  return (
    <li>
      <button
        type="button"
        className="festapp-event-performer"
        onClick={onClick}
      >
        <div className="festapp-event-performer-image-container">
          <ImageWithSkeleton
            src={artistImageUrl}
            alt={artist.name}
            className="festapp-event-performer-image"
          />
        </div>
        <span className="festapp-event-performer-name">{artist.name}</span>
      </button>
    </li>
  );
}

function getLinkIcon(url: string): {
  Icon: ComponentType<{ className?: string }>;
  label: string;
} {
  if (url.includes("instagram")) return { Icon: Instagram, label: "Instagram" };
  if (url.includes("facebook")) return { Icon: Facebook, label: "Facebook" };
  if (url.includes("twitter") || url.includes("x.com"))
    return { Icon: X, label: "X" };
  if (url.includes("tiktok")) return { Icon: TikTok, label: "TikTok" };
  if (url.includes("soundcloud"))
    return { Icon: Soundcloud, label: "SoundCloud" };
  if (url.includes("spotify")) return { Icon: Spotify, label: "Spotify" };
  if (url.includes("apple") && url.includes("music"))
    return { Icon: AppleMusic, label: "Apple Music" };
  if (url.includes("deezer")) return { Icon: Deezer, label: "Deezer" };
  if (url.includes("tidal")) return { Icon: Tidal, label: "Tidal" };
  if (url.includes("last.fm") || url.includes("lastfm"))
    return { Icon: LastFm, label: "Last.fm" };
  if (url.includes("musicbrainz"))
    return { Icon: MusicBrainz, label: "MusicBrainz" };
  if (url.includes("wikipedia")) return { Icon: Wikipedia, label: "Wikipedia" };
  if (url.includes("youtube") || url.includes("youtu.be"))
    return { Icon: YouTube, label: "YouTube" };
  return { Icon: Unkown, label: "Website" };
}
